import {
  PutRequest,
  useVbsDefaultsPutMutation,
  useVbsDefaultsShowQuery,
} from "@/services/backend/vbs/defaults/api/defaults-api";
import { Defaults, Key } from "@/services/backend/vbs/defaults/types/defaults";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { useEffect, useState } from "react";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import { Input } from "@/shared/components/ui/input";
import { Calculation } from "@/services/backend/vbs/defaults/types/calculations";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { Button } from "@/shared/components/ui/button";
import { Kind } from "@/services/backend/vbs/treatments/types/component";
import {
  AccessPanel,
  AerosolSeparator,
  AirHandlingUnit,
  AluminiumMeshGreaseFilter,
  Anemostat,
  DummyPlate,
  ExhaustDuctAngular,
  ExhaustDuctRound,
  ExhaustHood,
  ExtraHours,
  Lighting,
  PassiveArea,
  SupplyDuctAngular,
  SupplyDuctRound,
  VentilatedCeiling,
} from "@/services/backend/vbs/treatments/types/formula";

export function CalculationRoute() {
  const { data, isLoading, error } = useVbsDefaultsShowQuery({
    key: Key.Calculation,
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading || !data) {
    return (
      <Card className="animate-pulse">
        <CardHeader>
          <CardTitle>{t("Allgemeine Daten")}</CardTitle>
          <CardDescription>
            {t(
              "Festlegung von allgemeinen Daten die für VBS Kalkulationen genutzt werden sollen.",
            )}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <GeneralDataCard defaults={data} />
      <ExpensesCard defaults={data} />
      <div className="xl:col-span-2">
        <FormulaCard defaults={data} />
      </div>
    </div>
  );
}

function GeneralDataCard({ defaults }: { defaults: Defaults }) {
  const [request, setRequest] = useState<PutRequest>(defaults);
  const mutation = useVbsDefaultsPutMutation();
  useDebouncedMutation(request, setRequest, mutation, defaults);

  const data = request.data as Calculation;
  const setData = (calc: Calculation) => {
    setRequest({
      ...request,
      data: calc,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Allgemeine Daten")}</CardTitle>
        <CardDescription>
          {t(
            "Festlegung von allgemeinen Daten die für VBS Kalkulationen genutzt werden sollen.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-full pl-6">{t("Parameter")}</TableHead>
              <TableHead>{t("Einheit")}</TableHead>
              <TableHead className="pr-6">{t("Standardwert")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="pl-6">{t("Stunden pro Manntag")}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {t("Std / Tag")}
              </TableCell>
              <TableCell className="pr-6">
                <Input
                  className="-my-2 h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  type="number"
                  value={data.hoursInManDay}
                  onChange={(e) =>
                    setData({
                      ...data,
                      hoursInManDay: e.target.valueAsNumber,
                    })
                  }
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="pl-6">{t("Manntagessatz")}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {t("€ / Tag")}
              </TableCell>
              <TableCell className="pr-6">
                <Input
                  className="-my-2 h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  type="number"
                  value={data.manDayRate}
                  onChange={(e) =>
                    setData({ ...data, manDayRate: e.target.valueAsNumber })
                  }
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="pl-6">{t("Materialpauschale")}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {t("%")}
              </TableCell>
              <TableCell className="pr-6">
                <Input
                  className="-my-2 h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  type="number"
                  value={data.materialFlatRate}
                  onChange={(e) =>
                    setData({
                      ...data,
                      materialFlatRate: e.target.valueAsNumber,
                    })
                  }
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function ExpensesCard({ defaults }: { defaults: Defaults }) {
  const [request, setRequest] = useState<PutRequest>(defaults);
  const mutation = useVbsDefaultsPutMutation();
  useDebouncedMutation(request, setRequest, mutation, defaults);

  const data = request.data as Calculation;
  const setData = (calc: Calculation) => {
    setRequest({
      ...request,
      data: calc,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Spesen")}</CardTitle>
        <CardDescription>
          {t(
            "Festlegung von Standardwerten für Spesen die für VBS Kalkulationen genutzt werden sollen.",
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-full pl-6">{t("Parameter")}</TableHead>
              <TableHead>{t("Einheit")}</TableHead>
              <TableHead className="pr-6">{t("Standardwert")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="pl-6">
                {t("Verpflegungspauschale Übernachtungsspesen")}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {t("€")}
              </TableCell>
              <TableCell className="pr-6">
                <Input
                  className="-my-2 h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  type="number"
                  value={data.expenses.overnightAllowance}
                  onChange={(e) =>
                    setData({
                      ...data,
                      expenses: {
                        ...data.expenses,
                        overnightAllowance: e.target.valueAsNumber,
                      },
                    })
                  }
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="pl-6">{t("Übernachtungskosten")}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {t("€")}
              </TableCell>
              <TableCell className="pr-6">
                <Input
                  className="-my-2 h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  type="number"
                  value={data.expenses.overnightHotelRate}
                  onChange={(e) =>
                    setData({
                      ...data,
                      expenses: {
                        ...data.expenses,
                        overnightHotelRate: e.target.valueAsNumber,
                      },
                    })
                  }
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="pl-6">
                {t("Verpflegungspauschale Tagesspesen")}
              </TableCell>
              <TableCell className="text-right whitespace-nowrap">
                {t("€")}
              </TableCell>
              <TableCell className="pr-6">
                <Input
                  className="-my-2 h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                  type="number"
                  value={data.expenses.perDiemAllowance}
                  onChange={(e) =>
                    setData({
                      ...data,
                      expenses: {
                        ...data.expenses,
                        perDiemAllowance: e.target.valueAsNumber,
                      },
                    })
                  }
                />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function FormulaCard({ defaults }: { defaults: Defaults }) {
  const [request, setRequest] = useState<PutRequest>(defaults);
  const mutation = useVbsDefaultsPutMutation();
  useDebouncedMutation(request, setRequest, mutation, defaults);

  useEffect(() => {
    setRequest((prevRequest) => {
      if (defaults !== prevRequest) {
        return defaults;
      }
      return prevRequest;
    });
  }, [defaults, setRequest]);

  const data = request.data as Calculation;
  const setData = (calc: Calculation) => {
    setRequest({
      ...request,
      data: calc,
    });
  };

  const updateFormulaParameters = <Parameter,>(
    formula: string,
    parameters: Parameter,
  ) => {
    setData({
      ...data,
      formulas: {
        ...data.formulas,
        [formula]: parameters,
      },
    });
  };

  return (
    <Card>
      <div className="flex items-start justify-between">
        <CardHeader>
          <CardTitle>{t("Formeln für Komponenten")}</CardTitle>
          <CardDescription>
            {t(
              "Festlegung von Standardwerten für die Formeln zur Berechnung der Stundensätze und Preise der Komponenten.",
            )}
          </CardDescription>
        </CardHeader>
        <div className="flex justify-end px-6 pt-6">
          <CopyHourlyRateButton defaults={defaults} />
        </div>
      </div>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-full pl-6">{t("Parameter")}</TableHead>
              <TableHead>{t("Einheit")}</TableHead>
              <TableHead className="pr-6">{t("Standardwert")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <VentilatedCeilingRow
              ventilatedCeiling={data.formulas.ventilatedCeiling}
              onChange={(ventilatedCeiling) =>
                updateFormulaParameters("ventilatedCeiling", ventilatedCeiling)
              }
            />
            <ExhaustDuctAngularRow
              exhaustDuctAngular={data.formulas.exhaustDuctAngular}
              onChange={(exhaustDuctAngular) =>
                updateFormulaParameters(
                  "exhaustDuctAngular",
                  exhaustDuctAngular,
                )
              }
            />
            <ExhaustDuctRoundRow
              exhaustDuctRound={data.formulas.exhaustDuctRound}
              onChange={(exhaustDuctRound) =>
                updateFormulaParameters("exhaustDuctRound", exhaustDuctRound)
              }
            />
            <AerosolSeparatorRow
              aerosolSeparator={data.formulas.aerosolSeparator}
              onChange={(aerosolSeparator) =>
                updateFormulaParameters("aerosolSeparator", aerosolSeparator)
              }
            />
            <AluminiumMeshGreaseFilterRow
              aluminiumMeshGreaseFilter={
                data.formulas.aluminiumMeshGreaseFilter
              }
              onChange={(aluminiumMeshGreaseFilter) =>
                updateFormulaParameters(
                  "aluminiumMeshGreaseFilter",
                  aluminiumMeshGreaseFilter,
                )
              }
            />
            <AnemostatRow
              anemostat={data.formulas.anemostat}
              onChange={(anemostat) =>
                updateFormulaParameters("anemostat", anemostat)
              }
            />
            <LightingRow
              lighting={data.formulas.lighting}
              onChange={(lighting) =>
                updateFormulaParameters("lighting", lighting)
              }
            />
            <DummyPlateRow
              dummyPlate={data.formulas.dummyPlate}
              onChange={(dummyPlate) =>
                updateFormulaParameters("dummyPlate", dummyPlate)
              }
            />
            <ExhaustHoodRow
              exhaustHood={data.formulas.exhaustHood}
              onChange={(exhaustHood) =>
                updateFormulaParameters("exhaustHood", exhaustHood)
              }
            />
            <PassiveAreaRow
              passiveArea={data.formulas.passiveArea}
              onChange={(passiveArea) =>
                updateFormulaParameters("passiveArea", passiveArea)
              }
            />
            <AccessPanelRow
              accessPanel={data.formulas.accessPanel}
              onChange={(accessPanel) =>
                updateFormulaParameters("accessPanel", accessPanel)
              }
            />
            <AirHandlingUnitRow
              airHandlingUnit={data.formulas.airHandlingUnit}
              onChange={(airHandlingUnit) =>
                updateFormulaParameters("airHandlingUnit", airHandlingUnit)
              }
            />
            <SupplyDuctAngularRow
              supplyDuctAngular={data.formulas.supplyDuctAngular}
              onChange={(supplyDuctAngular) =>
                updateFormulaParameters("supplyDuctAngular", supplyDuctAngular)
              }
            />
            <SupplyDuctRoundRow
              supplyDuctRound={data.formulas.supplyDuctRound}
              onChange={(supplyDuctRound) =>
                updateFormulaParameters("supplyDuctRound", supplyDuctRound)
              }
            />
            <ExtraHoursRow
              extraHours={data.formulas.extraHours}
              onChange={(extraHours) =>
                updateFormulaParameters("extraHours", extraHours)
              }
            />
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function CopyHourlyRateButton({ defaults }: { defaults: Defaults }) {
  const [put, { isLoading, error, reset }] = useVbsDefaultsPutMutation();
  const { toast } = useToast();

  useEffect(() => {
    if (error) {
      toast({
        ...parseRTKQueryError(error),
      });
      reset();
    }
  }, [error, toast, reset]);

  const onClick = () => {
    if (isLoading) {
      return;
    }

    const data = defaults.data as Calculation;
    if (data.hoursInManDay === 0) {
      return;
    }

    const hourlyRate = parseFloat(
      (data.manDayRate / data.hoursInManDay).toFixed(2),
    );

    put({
      ...defaults,
      data: {
        ...data,
        formulas: {
          accessPanel: {
            ...data.formulas.accessPanel,
          },
          aerosolSeparator: {
            ...data.formulas.aerosolSeparator,
            hourlyRate,
          },
          airHandlingUnit: {
            ...data.formulas.airHandlingUnit,
            hourlyRate,
          },
          aluminiumMeshGreaseFilter: {
            ...data.formulas.aluminiumMeshGreaseFilter,
            hourlyRate,
          },
          anemostat: {
            ...data.formulas.anemostat,
            hourlyRate,
          },
          dummyPlate: {
            ...data.formulas.dummyPlate,
            hourlyRate,
          },
          exhaustDuctAngular: {
            ...data.formulas.exhaustDuctAngular,
            hourlyRate,
          },
          exhaustDuctRound: {
            ...data.formulas.exhaustDuctRound,
            hourlyRate,
          },
          exhaustHood: {
            ...data.formulas.exhaustHood,
            hourlyRate,
          },
          extraHours: {
            // extra hours always have an individual rate
            // as of 03.2024.
            ...data.formulas.extraHours,
          },
          freeText: {
            ...data.formulas.freeText,
          },
          lighting: {
            ...data.formulas.lighting,
            hourlyRate,
          },
          passiveArea: {
            ...data.formulas.passiveArea,
            hourlyRate,
          },
          supplyDuctAngular: {
            ...data.formulas.supplyDuctAngular,
            hourlyRate,
          },
          supplyDuctRound: {
            ...data.formulas.supplyDuctRound,
            hourlyRate,
          },
          ventilatedCeiling: {
            ...data.formulas.ventilatedCeiling,
            hourlyRate,
          },
        },
      },
    });
  };

  return (
    <Button variant="outline" onClick={onClick} disabled={isLoading}>
      {t("Stundensatz aus Allgemeinen Daten übernehmen")}
    </Button>
  );
}

function AccessPanelRow({
  accessPanel,
  onChange,
}: {
  accessPanel: AccessPanel;
  onChange: (accessPanel: AccessPanel) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6">{t(Kind.AccessPanel)}</TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {t("€ / Stück")}
      </TableCell>
      <TableCell className="pr-6">
        <Input
          className="-my-2 h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={accessPanel.pricePerPanel}
          onChange={(e) =>
            onChange({ ...accessPanel, pricePerPanel: e.target.valueAsNumber })
          }
        />
      </TableCell>
    </TableRow>
  );
}

function AerosolSeparatorRow({
  aerosolSeparator,
  onChange,
}: {
  aerosolSeparator: AerosolSeparator;
  onChange: (aerosolSeparator: AerosolSeparator) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6 align-text-top">
        {t(Kind.AerosolSeparator)}
      </TableCell>
      <TableCell className="space-y-2 text-right whitespace-nowrap">
        <div className="h-8 pt-1">{t("Minuten / Stück")}</div>
        <div className="h-8 pt-1">{t("€ / Stunde")}</div>
      </TableCell>
      <TableCell className="space-y-2 pr-6">
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={aerosolSeparator.minutesPerCount}
          onChange={(e) =>
            onChange({
              ...aerosolSeparator,
              minutesPerCount: e.target.valueAsNumber,
            })
          }
        />
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={aerosolSeparator.hourlyRate}
          onChange={(e) =>
            onChange({
              ...aerosolSeparator,
              hourlyRate: e.target.valueAsNumber,
            })
          }
        />
      </TableCell>
    </TableRow>
  );
}

function AirHandlingUnitRow({
  airHandlingUnit,
  onChange,
}: {
  airHandlingUnit: AirHandlingUnit;
  onChange: (accessPanel: AirHandlingUnit) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6">{t(Kind.AirHandlingUnit)}</TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {t("€ / Stunde")}
      </TableCell>
      <TableCell className="pr-6">
        <Input
          className="-my-2 h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={airHandlingUnit.hourlyRate}
          onChange={(e) =>
            onChange({
              ...airHandlingUnit,
              hourlyRate: e.target.valueAsNumber,
            })
          }
        />
      </TableCell>
    </TableRow>
  );
}

function AluminiumMeshGreaseFilterRow({
  aluminiumMeshGreaseFilter,
  onChange,
}: {
  aluminiumMeshGreaseFilter: AluminiumMeshGreaseFilter;
  onChange: (accessPanel: AluminiumMeshGreaseFilter) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6 align-text-top">
        {t(Kind.AluminiumMeshGreaseFilter)}
      </TableCell>
      <TableCell className="space-y-2 text-right whitespace-nowrap">
        <div className="h-8 pt-1">{t("Minuten / Stück")}</div>
        <div className="h-8 pt-1">{t("€ / Stunde")}</div>
      </TableCell>
      <TableCell className="space-y-2 pr-6">
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={aluminiumMeshGreaseFilter.minutesPerCount}
          onChange={(e) =>
            onChange({
              ...aluminiumMeshGreaseFilter,
              minutesPerCount: e.target.valueAsNumber,
            })
          }
        />
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={aluminiumMeshGreaseFilter.hourlyRate}
          onChange={(e) =>
            onChange({
              ...aluminiumMeshGreaseFilter,
              hourlyRate: e.target.valueAsNumber,
            })
          }
        />
      </TableCell>
    </TableRow>
  );
}

function AnemostatRow({
  anemostat,
  onChange,
}: {
  anemostat: Anemostat;
  onChange: (accessPanel: Anemostat) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6 align-text-top">{t(Kind.Anemostat)}</TableCell>
      <TableCell className="space-y-2 text-right whitespace-nowrap">
        <div className="h-8 pt-1">{t("Minuten / Stück")}</div>
        <div className="h-8 pt-1">{t("€ / Stunde")}</div>
      </TableCell>
      <TableCell className="space-y-2 pr-6">
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={anemostat.minutesPerCount}
          onChange={(e) =>
            onChange({
              ...anemostat,
              minutesPerCount: e.target.valueAsNumber,
            })
          }
        />
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={anemostat.hourlyRate}
          onChange={(e) =>
            onChange({
              ...anemostat,
              hourlyRate: e.target.valueAsNumber,
            })
          }
        />
      </TableCell>
    </TableRow>
  );
}

function DummyPlateRow({
  dummyPlate,
  onChange,
}: {
  dummyPlate: DummyPlate;
  onChange: (accessPanel: DummyPlate) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6 align-text-top">
        {t(Kind.DummyPlate)}
      </TableCell>
      <TableCell className="space-y-2 text-right whitespace-nowrap">
        <div className="h-8 pt-1">{t("Minuten / Stück")}</div>
        <div className="h-8 pt-1">{t("€ / Stunde")}</div>
      </TableCell>
      <TableCell className="space-y-2 pr-6">
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={dummyPlate.minutesPerCount}
          onChange={(e) =>
            onChange({
              ...dummyPlate,
              minutesPerCount: e.target.valueAsNumber,
            })
          }
        />
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={dummyPlate.hourlyRate}
          onChange={(e) =>
            onChange({
              ...dummyPlate,
              hourlyRate: e.target.valueAsNumber,
            })
          }
        />
      </TableCell>
    </TableRow>
  );
}

function ExhaustDuctAngularRow({
  exhaustDuctAngular,
  onChange,
}: {
  exhaustDuctAngular: ExhaustDuctAngular;
  onChange: (exhaustDuctAngular: ExhaustDuctAngular) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6 align-text-top">
        {t(Kind.ExhaustDuctAngular)}
      </TableCell>
      <TableCell className="space-y-2 text-right whitespace-nowrap">
        <div className="h-28 pt-1">{t("Minuten / Quadratmeter")}</div>
        <div className="h-8 pt-1">{t("€ / Stunde")}</div>
      </TableCell>
      <TableCell className="space-y-2 pr-6">
        {exhaustDuctAngular.buildUps.map(({ buildUp, minutesPerMeter }) => (
          <div key={buildUp} className="flex justify-between space-x-4">
            <span className="h-8 pt-1">{t(buildUp)}</span>
            <Input
              className="h-8 min-w-24 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              type="number"
              value={minutesPerMeter}
              onChange={(e) =>
                onChange({
                  ...exhaustDuctAngular,
                  buildUps: exhaustDuctAngular.buildUps.map((b) => {
                    if (b.buildUp === buildUp) {
                      return {
                        ...b,
                        minutesPerMeter: e.target.valueAsNumber,
                      };
                    }
                    return b;
                  }),
                })
              }
            />
          </div>
        ))}
        <Input
          className="h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={exhaustDuctAngular.hourlyRate}
          onChange={(e) =>
            onChange({
              ...exhaustDuctAngular,
              hourlyRate: e.target.valueAsNumber,
            })
          }
        />
      </TableCell>
    </TableRow>
  );
}

function ExhaustDuctRoundRow({
  exhaustDuctRound,
  onChange,
}: {
  exhaustDuctRound: ExhaustDuctRound;
  onChange: (exhaustDuctAngular: ExhaustDuctRound) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6 align-text-top">
        {t(Kind.ExhaustDuctRound)}
      </TableCell>
      <TableCell className="space-y-2 text-right whitespace-nowrap">
        <div className="h-28 pt-1">{t("Minuten / Quadratmeter")}</div>
        <div className="h-8 pt-1">{t("€ / Stunde")}</div>
      </TableCell>
      <TableCell className="space-y-2 pr-6">
        {exhaustDuctRound.buildUps.map(({ buildUp, minutesPerMeter }) => (
          <div key={buildUp} className="flex justify-between space-x-4">
            <span className="h-8 pt-1">{t(buildUp)}</span>
            <Input
              className="h-8 min-w-24 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
              type="number"
              value={minutesPerMeter}
              onChange={(e) =>
                onChange({
                  ...exhaustDuctRound,
                  buildUps: exhaustDuctRound.buildUps.map((b) => {
                    if (b.buildUp === buildUp) {
                      return {
                        ...b,
                        minutesPerMeter: e.target.valueAsNumber,
                      };
                    }
                    return b;
                  }),
                })
              }
            />
          </div>
        ))}
        <Input
          className="h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={exhaustDuctRound.hourlyRate}
          onChange={(e) =>
            onChange({
              ...exhaustDuctRound,
              hourlyRate: e.target.valueAsNumber,
            })
          }
        />
      </TableCell>
    </TableRow>
  );
}

function ExhaustHoodRow({
  exhaustHood,
  onChange,
}: {
  exhaustHood: ExhaustHood;
  onChange: (accessPanel: ExhaustHood) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6 align-text-top">
        {t(Kind.ExhaustHood)}
      </TableCell>
      <TableCell className="space-y-2 text-right whitespace-nowrap">
        <div className="h-8 pt-1">{t("Minuten / Quadratmeter")}</div>
        <div className="h-8 pt-1">{t("€ / Stunde")}</div>
      </TableCell>
      <TableCell className="space-y-2 pr-6">
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={exhaustHood.minutesPerSquareMeter}
          onChange={(e) =>
            onChange({
              ...exhaustHood,
              minutesPerSquareMeter: e.target.valueAsNumber,
            })
          }
        />
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={exhaustHood.hourlyRate}
          onChange={(e) =>
            onChange({
              ...exhaustHood,
              hourlyRate: e.target.valueAsNumber,
            })
          }
        />
      </TableCell>
    </TableRow>
  );
}

function ExtraHoursRow({
  extraHours,
  onChange,
}: {
  extraHours: ExtraHours;
  onChange: (extraHours: ExtraHours) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6">{t(Kind.ExtraHours)}</TableCell>
      <TableCell className="text-right whitespace-nowrap">
        {t("€ / Stunde")}
      </TableCell>
      <TableCell className="pr-6">
        <Input
          className="-my-2 h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={extraHours.hourlyRate}
          onChange={(e) =>
            onChange({ ...extraHours, hourlyRate: e.target.valueAsNumber })
          }
        />
      </TableCell>
    </TableRow>
  );
}

function LightingRow({
  lighting,
  onChange,
}: {
  lighting: Lighting;
  onChange: (accessPanel: Lighting) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6 align-text-top">{t(Kind.Lighting)}</TableCell>
      <TableCell className="space-y-2 text-right whitespace-nowrap">
        <div className="h-8 pt-1">{t("Minuten / Stück")}</div>
        <div className="h-8 pt-1">{t("€ / Stunde")}</div>
      </TableCell>
      <TableCell className="space-y-2 pr-6">
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={lighting.minutesPerCount}
          onChange={(e) =>
            onChange({
              ...lighting,
              minutesPerCount: e.target.valueAsNumber,
            })
          }
        />
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={lighting.hourlyRate}
          onChange={(e) =>
            onChange({
              ...lighting,
              hourlyRate: e.target.valueAsNumber,
            })
          }
        />
      </TableCell>
    </TableRow>
  );
}

function PassiveAreaRow({
  passiveArea,
  onChange,
}: {
  passiveArea: PassiveArea;
  onChange: (accessPanel: PassiveArea) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6 align-text-top">
        {t(Kind.PassiveArea)}
      </TableCell>
      <TableCell className="space-y-2 text-right whitespace-nowrap">
        <div className="h-8 pt-1">{t("Minuten / Quadratmeter")}</div>
        <div className="h-8 pt-1">{t("€ / Stunde")}</div>
      </TableCell>
      <TableCell className="space-y-2 pr-6">
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={passiveArea.minutesPerSquareMeter}
          onChange={(e) =>
            onChange({
              ...passiveArea,
              minutesPerSquareMeter: e.target.valueAsNumber,
            })
          }
        />
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={passiveArea.hourlyRate}
          onChange={(e) =>
            onChange({
              ...passiveArea,
              hourlyRate: e.target.valueAsNumber,
            })
          }
        />
      </TableCell>
    </TableRow>
  );
}

function SupplyDuctAngularRow({
  supplyDuctAngular,
  onChange,
}: {
  supplyDuctAngular: SupplyDuctAngular;
  onChange: (accessPanel: SupplyDuctAngular) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6 align-text-top">
        {t(Kind.SupplyDuctAngular)}
      </TableCell>
      <TableCell className="space-y-2 text-right whitespace-nowrap">
        <div className="h-8 pt-1">{t("Minuten / Meter")}</div>
        <div className="h-8 pt-1">{t("€ / Stunde")}</div>
      </TableCell>
      <TableCell className="space-y-2 pr-6">
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={supplyDuctAngular.minutesPerMeter}
          onChange={(e) =>
            onChange({
              ...supplyDuctAngular,
              minutesPerMeter: e.target.valueAsNumber,
            })
          }
        />
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={supplyDuctAngular.hourlyRate}
          onChange={(e) =>
            onChange({
              ...supplyDuctAngular,
              hourlyRate: e.target.valueAsNumber,
            })
          }
        />
      </TableCell>
    </TableRow>
  );
}

function SupplyDuctRoundRow({
  supplyDuctRound,
  onChange,
}: {
  supplyDuctRound: SupplyDuctRound;
  onChange: (accessPanel: SupplyDuctRound) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6 align-text-top">
        {t(Kind.SupplyDuctRound)}
      </TableCell>
      <TableCell className="space-y-2 text-right whitespace-nowrap">
        <div className="h-8 pt-1">{t("Minuten / Meter")}</div>
        <div className="h-8 pt-1">{t("€ / Stunde")}</div>
      </TableCell>
      <TableCell className="space-y-2 pr-6">
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={supplyDuctRound.minutesPerMeter}
          onChange={(e) =>
            onChange({
              ...supplyDuctRound,
              minutesPerMeter: e.target.valueAsNumber,
            })
          }
        />
        <Input
          className="block h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={supplyDuctRound.hourlyRate}
          onChange={(e) =>
            onChange({
              ...supplyDuctRound,
              hourlyRate: e.target.valueAsNumber,
            })
          }
        />
      </TableCell>
    </TableRow>
  );
}

function VentilatedCeilingRow({
  ventilatedCeiling,
  onChange,
}: {
  ventilatedCeiling: VentilatedCeiling;
  onChange: (ventilatedCeiling: VentilatedCeiling) => void;
}) {
  return (
    <TableRow>
      <TableCell className="pl-6 align-text-top">
        {t(Kind.VentilatedCeiling)}
      </TableCell>
      <TableCell className="space-y-2 text-right whitespace-nowrap">
        <div className="h-28 pt-1">{t("Minuten / Quadratmeter")}</div>
        <div className="h-8 pt-1">{t("€ / Stunde")}</div>
      </TableCell>
      <TableCell className="space-y-2 pr-6">
        {ventilatedCeiling.constructions.map(
          ({ construction, minutesPerSquareMeter }) => (
            <div key={construction} className="flex justify-between space-x-4">
              <span className="h-8 pt-1">{t(construction)}</span>
              <Input
                className="h-8 min-w-24 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                type="number"
                value={minutesPerSquareMeter}
                onChange={(e) =>
                  onChange({
                    ...ventilatedCeiling,
                    constructions: ventilatedCeiling.constructions.map((c) => {
                      if (c.construction === construction) {
                        return {
                          ...c,
                          minutesPerSquareMeter: e.target.valueAsNumber,
                        };
                      }
                      return c;
                    }),
                  })
                }
              />
            </div>
          ),
        )}
        <Input
          className="h-8 [appearance:textfield] text-right [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          type="number"
          value={ventilatedCeiling.hourlyRate}
          onChange={(e) =>
            onChange({
              ...ventilatedCeiling,
              hourlyRate: e.target.valueAsNumber,
            })
          }
        />
      </TableCell>
    </TableRow>
  );
}
